// Store bytes instead of Blob/File objects: Safari can reject their IDB serialization.
const marker = '__okprintStoredFileV1';
export function encodeImages(value) {
  if (value instanceof Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Photo read failed'));
      reader.onabort = () => reject(new Error('Photo read aborted'));
      reader.onload = () => {
        if (reader.result.byteLength !== value.size) return reject(new Error('Incomplete photo read'));
        resolve({ [marker]: true, bytes: reader.result, mime: value.type,
          name: value instanceof File ? value.name : null,
          lastModified: value instanceof File ? value.lastModified : null });
      };
      reader.readAsArrayBuffer(value);
    });
  }
  if (Array.isArray(value)) return Promise.all(value.map(encodeImages));
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    // Snapshot entries synchronously, including Redux/Immer values.
    return Promise.all(Object.entries(value).map(async ([key, item]) => [key, await encodeImages(item)]))
      .then(Object.fromEntries);
  }
  return Promise.resolve(value);
}
export function decodeImages(value) {
  if (value && value[marker] === true && value.bytes instanceof ArrayBuffer) {
    return value.name === null ? new Blob([value.bytes], { type: value.mime }) :
      new File([value.bytes], value.name, { type: value.mime, lastModified: value.lastModified });
  }
  if (Array.isArray(value)) return value.map(decodeImages);
  if (value && Object.getPrototypeOf(value) === Object.prototype)
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, decodeImages(item)]));
  return value; // Existing File/Blob records remain readable.
}
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('imageDatabase', 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains('images'))
        request.result.createObjectStore('images', { keyPath: 'type' });
    };
  });
}
async function transact(mode, action) {
  const db = await openDB();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction('images', mode);
      let result, failure;
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(failure || tx.error || new Error('Photo storage transaction aborted'));
      const fail = error => { failure = error; tx.abort(); };
      try { action(tx.objectStore('images'), value => { result = value; }, fail); }
      catch (error) { fail(error); }
    });
  } finally { db.close(); }
}
function readRaw(type) {
  return transact('readonly', (store, done) => {
    const request = store.get(type);
    request.onsuccess = () => done(request.result);
  });
}
export async function readImageRecord(type) {
  return decodeImages(await readRaw(type));
}
export async function updateImageRecord(type, update) {
  // File reads must happen outside a transaction. Recheck the revision atomically
  // before writing so concurrent tabs/imports cannot overwrite newer changes.
  for (let attempt = 0; attempt < 8; attempt++) {
    const before = await readRaw(type);
    const encoded = await encodeImages(update(decodeImages(before)));
    const committed = await transact('readwrite', (store, done, fail) => {
      const request = store.get(type);
      request.onsuccess = () => {
        try {
          const current = request.result;
          if (Boolean(current) !== Boolean(before) || current?._storageRevision !== before?._storageRevision) {
            done(false); return;
          }
          store.put({ ...encoded, type, _storageRevision: (before?._storageRevision || 0) + 1 });
          done(true);
        } catch (error) { fail(error); }
      };
    });
    if (committed) return;
  }
  throw new Error('Photos changed in another tab. Please retry.');
}
export function deleteImageRecord(type) {
  if (!type) return Promise.reject(new Error('No key specified for deletion'));
  return transact('readwrite', store => { store.delete(type); });
}
